import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Sparkles, BookOpen, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import DateInput from "@/components/DateInput";
import ProjectSelect from "@/components/ProjectSelect";
import { SiteLogReport } from "@/components/SiteLogReport";
import { Markdown } from "@/components/Markdown";
import { fmtDate, todayISO } from "@/lib/utils";

const EMPTY = { project_id: "", log_date: todayISO(), weather: "", temperature: "", workforce_count: 0, activities: "", issues: "", materials_used: "", equipment: "" };

export default function SiteLogs() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [aiLoading, setAiLoading] = useState(null);

  const load = () => api.get(`/site-logs${filter ? `?project_id=${filter}` : ""}`).then(r => setRows(r.data));
  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);
  useEffect(() => { load(); }, [filter]);

  const save = async () => {
    if (!form.project_id || !form.log_date) { toast.error("Proje ve tarih zorunlu"); return; }
    await api.post("/site-logs", { ...form, workforce_count: Number(form.workforce_count) || 0 });
    toast.success("Günlük kaydedildi"); setOpen(false); setForm(EMPTY); load();
  };
  const remove = async (id) => { await api.delete(`/site-logs/${id}`); toast.success("Silindi"); load(); };

  const runAI = async (id) => {
    setAiLoading(id);
    try {
      const r = await api.post(`/site-logs/${id}/ai-summary`);
      toast.success("AI özeti üretildi");
      setRows(rows.map(x => x.id === id ? { ...x, ai_summary: r.data.ai_summary } : x));
    } catch (e) {
      toast.error("AI özeti üretilemedi: " + (e.response?.data?.detail || e.message));
    } finally { setAiLoading(null); }
  };

  return (
    <div data-testid="sitelogs-page">
      <PageHeader title="Şantiye Günlüğü" subtitle="Günlük saha raporları — AI destekli özet ile.">
        <ProjectSelect projects={projects} value={filter} onChange={setFilter} testid="filter-project" />
        <SiteLogReport projects={projects} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-sitelog-button" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"><Plus className="w-4 h-4 mr-1" /> Yeni Günlük</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Yeni Şantiye Günlüğü</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Proje *</Label><ProjectSelect projects={projects} value={form.project_id} onChange={v => setForm({...form, project_id: v})} includeAll={false} testid="form-project" /></div>
              <div><Label>Tarih *</Label><DateInput data-testid="input-date" value={form.log_date} onChange={e => setForm({...form, log_date: e.target.value})} /></div>
              <div><Label>Hava Durumu</Label><Input value={form.weather} onChange={e => setForm({...form, weather: e.target.value})} placeholder="Güneşli / Yağmurlu..." /></div>
              <div><Label>Sıcaklık</Label><Input value={form.temperature} onChange={e => setForm({...form, temperature: e.target.value})} placeholder="22°C" /></div>
              <div><Label>İşgücü Sayısı</Label><Input type="number" value={form.workforce_count} onChange={e => setForm({...form, workforce_count: e.target.value})} /></div>
              <div className="col-span-2"><Label>Yapılan Faaliyetler</Label><Textarea rows={3} data-testid="input-activities" value={form.activities} onChange={e => setForm({...form, activities: e.target.value})} /></div>
              <div className="col-span-2"><Label>Kullanılan Malzemeler</Label><Textarea rows={2} value={form.materials_used} onChange={e => setForm({...form, materials_used: e.target.value})} /></div>
              <div className="col-span-2"><Label>Ekipman</Label><Input value={form.equipment} onChange={e => setForm({...form, equipment: e.target.value})} placeholder="Vinç, kazıcı, karıştırıcı..." /></div>
              <div className="col-span-2"><Label>Problem / Notlar</Label><Textarea rows={2} value={form.issues} onChange={e => setForm({...form, issues: e.target.value})} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>İptal</Button><Button className="bg-amber-500 hover:bg-amber-600 text-slate-950" onClick={save} data-testid="save-sitelog-button">Kaydet</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <BookOpen className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">Henüz günlük girilmemiş</div>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map(r => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-5" data-testid={`sitelog-${r.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="font-display font-bold text-lg">{fmtDate(r.log_date)}</div>
                  <div className="text-xs text-muted-foreground flex gap-3 mt-1 flex-wrap">
                    <span>Hava: <strong className="text-foreground">{r.weather || "-"}</strong></span>
                    <span>Sıcaklık: <strong className="text-foreground">{r.temperature || "-"}</strong></span>
                    <span>İşçi: <strong className="text-amber-600 dark:text-amber-400 font-mono-nums">{r.workforce_count}</strong></span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => runAI(r.id)} disabled={aiLoading === r.id} data-testid={`ai-summary-${r.id}`}>
                    {aiLoading === r.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1 text-amber-500" />}
                    AI Özet
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <Field label="Yapılan Faaliyetler" value={r.activities} />
                <Field label="Kullanılan Malzemeler" value={r.materials_used} />
                <Field label="Ekipman" value={r.equipment} />
                <Field label="Problemler / Notlar" value={r.issues} />
              </div>
              {r.ai_summary && (
                <div className="mt-4 rounded-lg bg-amber-500/10 border border-amber-500/30 p-4">
                  <div className="flex items-center gap-2 mb-2 text-amber-700 dark:text-amber-400 font-semibold text-sm"><Sparkles className="w-4 h-4" /> AI Günlük Özeti</div>
                  <div className="text-sm"><Markdown>{r.ai_summary}</Markdown></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-1">{label}</div>
      <div className="text-foreground whitespace-pre-wrap">{value || "-"}</div>
    </div>
  );
}
